import {
    Healing,
    HealthAndSafety,
    LocalHospital,
    MedicalServices,
    MonitorHeart,
    Vaccines
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Container,
    createTheme,
    CssBaseline,
    Grid,
    ThemeProvider,
    Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';


const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#145db8',
      light: '#4d8be0',
      dark: '#0f448f',
    },
    secondary: {
      main: '#ffffff',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#145db8',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 500,
    },
  },
});

const MainContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: `
    linear-gradient(135deg, rgba(20, 93, 184, 0.1) 0%, rgba(255, 255, 255, 0.9) 50%, rgba(20, 93, 184, 0.05) 100%),
    url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23145db8' fill-opacity='0.03'%3E%3Cpath d='M30 30c0-16.569 13.431-30 30-30s30 13.431 30 30-13.431 30-30 30-30-13.431-30-30z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
  `,
  backgroundSize: '120px 120px',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 20%, rgba(20, 93, 184, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(20, 93, 184, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 60% 40%, rgba(20, 93, 184, 0.05) 0%, transparent 50%)
    `,
    zIndex: 1,
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
}));

const HospitalIcon = styled(Box)(({ theme }) => ({
  width: 140,
  height: 140,
  background: 'linear-gradient(135deg, #145db8 0%, #4d8be0 100%)',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 20px 40px rgba(20, 93, 184, 0.3)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #145db8 0%, #4d8be0 100%)',
    opacity: 0.3,
    filter: 'blur(20px)',
    zIndex: -1,
  },
}));

const PulseRing = styled(motion.div)(({ theme }) => ({
  position: 'absolute',
  width: 180,
  height: 180,
  border: '3px solid #145db8',
  borderRadius: '50%',
  opacity: 0.6,
}));

const FloatingElement = styled(motion.div)(({ theme }) => ({
  position: 'absolute',
  width: 60,
  height: 60,
  borderRadius: '50%',
  background: 'rgba(20, 93, 184, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(20, 93, 184, 0.2)',
  zIndex: 2,
}));

const CrackerParticle = styled(motion.div)(({ theme }) => ({
  position: 'absolute',
  width: 8,
  height: 8,
  borderRadius: '50%',
  left: '50%',
  top: '50%',
  zIndex: 10,
}));

const CornerCrackerParticle = styled(motion.div)(({ theme }) => ({
  position: 'absolute',
  width: 8,
  height: 8,
  borderRadius: '50%',
  zIndex: 15,
}));

const SparkleElement = styled(motion.div)(({ theme }) => ({
  position: 'absolute',
  width: 4,
  height: 4,
  background: '#145db8',
  borderRadius: '50%',
  zIndex: 5,
}));

const ColorBubble = styled(motion.div)(({ theme }) => ({
  position: 'absolute',
  borderRadius: '50%',
  zIndex: 1,
  filter: 'blur(1px)',
  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
}));

const CrackerLaunchComponent = () => {
  const [currentStep, setCurrentStep] = useState('countdown');
  const [countdownNumber, setCountdownNumber] = useState(5);
  const [showParticles, setShowParticles] = useState(false);
  const [textStep, setTextStep] = useState(0);
  const [showFinalBurst, setShowFinalBurst] = useState(false);
  const [showCornerCrackers, setShowCornerCrackers] = useState(false);
  const [cornerCrackerKey, setCornerCrackerKey] = useState(0);

  useEffect(() => {
    if (currentStep === 'countdown') {
      const timer = setInterval(() => {
        setCountdownNumber(prev => {
          if (prev === 1) {
            clearInterval(timer);
            setCurrentStep('reveal');
            return 0;
          }
          return prev - 1;
        });
      }, 2500); // Extended countdown - 2.5 seconds each
      return () => clearInterval(timer);
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === 'reveal') {
      const timers = [
        setTimeout(() => setTextStep(1), 1500), // Extended delay for first text
        setTimeout(() => setTextStep(2), 4000), // Extended delay for second text
        setTimeout(() => setTextStep(3), 7000), // Extended delay for third text
        setTimeout(() => {
          // Start corner crackers
          setShowCornerCrackers(true);
        }, 8000), // Extended delay for corner crackers
        setTimeout(() => {
          // Final burst before redirect
          setShowFinalBurst(true);
          setShowParticles(true);
        }, 12000), // Extended delay for final burst
        setTimeout(() => {
          // Navigate to login page
          window.location.href = '/login';
        }, 18000) // Extended total time - 18 seconds
      ];
      return () => timers.forEach(clearTimeout);
    }
  }, [currentStep]);

  // Continuous corner cracker effect
  useEffect(() => {
    if (showCornerCrackers) {
      const interval = setInterval(() => {
        setCornerCrackerKey(prev => prev + 1);
      }, 1000); // More frequent bursts - every 1 second
      return () => clearInterval(interval);
    }
  }, [showCornerCrackers]);

  const iconVariants = {
    initial: { scale: 1, rotate: 0 },
    shake: { 
      scale: [1, 1.2, 1, 1.2, 1, 1.1, 1], 
      rotate: [-8, 8, -8, 8, -5, 5, 0],
      transition: { duration: 1.2, repeat: 3 } // Extended shake duration and repeats
    },
    burst: { 
      scale: [1, 1.8, 1.3], 
      rotate: [0, 180, 360],
      transition: { duration: 2, ease: "easeInOut" } // Extended burst duration
    }
  };

  const crackerVariants = {
    initial: { scale: 0, opacity: 0 },
    animate: (i) => ({
      scale: [0, 1.2, 0.8, 0],
      opacity: [0, 1, 0.9, 0],
      x: [0, Math.cos(i * 0.4) * (200 + Math.random() * 400)], // Increased spread
      y: [0, Math.sin(i * 0.4) * (200 + Math.random() * 400)], // Increased spread
      rotate: [0, 360 * 3], // More rotations
      transition: { 
        duration: 5, // Extended duration
        ease: "easeOut",
        delay: Math.random() * 0.5
      }
    })
  };

  const cornerCrackerVariants = {
    initial: { scale: 0, opacity: 0 },
    animate: (custom) => {
      const { corner, index } = custom;
      const baseAngle = corner * 90;
      const spreadAngle = (index * 20) - 80; // Wider spread
      const angle = (baseAngle + spreadAngle) * (Math.PI / 180);
      const distance = 300 + Math.random() * 500; // Increased distance
      
      return {
        scale: [0, 1.5, 1, 0],
        opacity: [0, 1, 0.8, 0],
        x: [0, Math.cos(angle) * distance],
        y: [0, Math.sin(angle) * distance],
        rotate: [0, 360 * 3], // More rotations
        transition: { 
          duration: 4, // Extended duration
          ease: "easeOut",
          delay: Math.random() * 0.3
        }
      };
    }
  };

  const sparkleVariants = {
    initial: { scale: 0, opacity: 0 },
    animate: (i) => ({
      scale: [0, 1.5, 0],
      opacity: [0, 1, 0],
      x: [0, Math.random() * 1200 - 600], // Increased spread
      y: [0, Math.random() * 1000 - 500], // Increased spread
      rotate: [0, 360 * 2], // More rotations
      transition: { 
        duration: 6, // Extended duration
        ease: "easeOut",
        delay: Math.random() * 1.2
      }
    })
  };

  const textVariants = {
    initial: { opacity: 0, y: 50, scale: 0.8 },
    animate: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { duration: 2, ease: "easeOut" } // Extended text animation
    }
  };

  const bubbleVariants = {
    animate: (i) => ({
      y: [0, -window.innerHeight - 100],
      x: [0, Math.sin(i * 0.3) * 50], // Increased movement
      rotate: [0, 360 * 2], // More rotations
      scale: [0.8, 1.5, 0.8], // Increased scale
      opacity: [0, 0.9, 0],
      transition: {
        duration: 12 + Math.random() * 6, // Extended duration
        repeat: Infinity,
        delay: Math.random() * 15,
        ease: "easeInOut"
      }
    })
  };

  const floatingIcons = [
    { icon: <Healing sx={{ fontSize: 24, color: '#145db8' }} />, delay: 0 },
    { icon: <MedicalServices sx={{ fontSize: 24, color: '#145db8' }} />, delay: 0.5 },
    { icon: <HealthAndSafety sx={{ fontSize: 24, color: '#145db8' }} />, delay: 1 },
    { icon: <MonitorHeart sx={{ fontSize: 24, color: '#145db8' }} />, delay: 1.5 },
    { icon: <Vaccines sx={{ fontSize: 24, color: '#145db8' }} />, delay: 2 },
  ];

  const crackerColors = [
    '#FF1744', '#00E676', '#2196F3', '#FF9800', '#E91E63', '#FFEB3B', '#9C27B0', '#00BCD4', '#FF5722', '#4CAF50'
  ];

  const bubbleColors = [
    '#FF1744', '#00E676', '#2196F3', '#FF9800', '#E91E63', '#FFEB3B', '#9C27B0', '#00BCD4', '#FF5722', '#4CAF50',
    '#F44336', '#8BC34A', '#03A9F4', '#FF6F00', '#E91E63', '#CDDC39', '#673AB7', '#00E5FF', '#FF3D00', '#76FF03',
    '#FF0080', '#00FFFF', '#FF4081', '#64DD17', '#FF1744', '#00E676', '#2196F3', '#FF9800', '#E91E63', '#FFEB3B'
  ];

  const cornerPositions = [
    { left: '5%', top: '5%' },
    { right: '5%', top: '5%' },
    { left: '5%', bottom: '5%' },
    { right: '5%', bottom: '5%' }
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <MainContainer>
        {/* Left Side Color Bubbles */}
        {[...Array(15)].map((_, i) => (
          <ColorBubble
            key={`left-${i}`}
            style={{
              left: `${Math.random() * 8}%`,
              top: `${110 + Math.random() * 20}%`,
              width: `${20 + Math.random() * 35}px`,
              height: `${20 + Math.random() * 35}px`,
              background: `linear-gradient(135deg, ${bubbleColors[i % bubbleColors.length]}, ${bubbleColors[(i + 1) % bubbleColors.length]})`,
              opacity: 0.9,
              boxShadow: `0 0 25px ${bubbleColors[i % bubbleColors.length]}50`,
            }}
            variants={bubbleVariants}
            animate="animate"
            custom={i}
          />
        ))}

        {/* Right Side Color Bubbles */}
        {[...Array(15)].map((_, i) => (
          <ColorBubble
            key={`right-${i}`}
            style={{
              right: `${Math.random() * 8}%`,
              top: `${110 + Math.random() * 20}%`,
              width: `${20 + Math.random() * 35}px`,
              height: `${20 + Math.random() * 35}px`,
              background: `linear-gradient(135deg, ${bubbleColors[(i + 10) % bubbleColors.length]}, ${bubbleColors[(i + 11) % bubbleColors.length]})`,
              opacity: 0.9,
              boxShadow: `0 0 25px ${bubbleColors[(i + 10) % bubbleColors.length]}50`,
            }}
            variants={bubbleVariants}
            animate="animate"
            custom={i + 15}
          />
        ))}

        {/* Additional Center Bubbles */}
        {[...Array(12)].map((_, i) => (
          <ColorBubble
            key={`center-${i}`}
            style={{
              left: `${20 + Math.random() * 60}%`,
              top: `${110 + Math.random() * 20}%`,
              width: `${15 + Math.random() * 25}px`,
              height: `${15 + Math.random() * 25}px`,
              background: `radial-gradient(circle, ${bubbleColors[(i + 5) % bubbleColors.length]}, ${bubbleColors[(i + 6) % bubbleColors.length]})`,
              opacity: 0.9,
              boxShadow: `0 0 20px ${bubbleColors[(i + 5) % bubbleColors.length]}50`,
            }}
            variants={bubbleVariants}
            animate="animate"
            custom={i + 30}
          />
        ))}

        {/* Background Sparkles */}
        {[...Array(40)].map((_, i) => (
          <SparkleElement
            key={i}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${4 + Math.random() * 4}px`,
              height: `${4 + Math.random() * 4}px`,
            }}
            animate={{
              scale: [0, 1.5, 0],
              opacity: [0, 0.8, 0],
              rotate: [0, 360],
            }}
            transition={{
              duration: 6 + Math.random() * 4, // Extended duration
              repeat: Infinity,
              delay: Math.random() * 8,
              ease: "easeInOut"
            }}
          />
        ))}

        {/* Floating Medical Icons */}
        {floatingIcons.map((item, i) => (
          <FloatingElement
            key={i}
            style={{
              left: `${10 + i * 18}%`,
              top: `${15 + (i % 2) * 70}%`,
            }}
            animate={{
              y: [0, -35, 0],
              x: [0, 18, 0],
              rotate: [0, 12, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 7 + Math.random() * 3, // Extended duration
              repeat: Infinity,
              delay: item.delay,
              ease: "easeInOut"
            }}
          >
            {item.icon}
          </FloatingElement>
        ))}

        {/* Continuous Corner Crackers */}
        <AnimatePresence>
          {showCornerCrackers && (
            <motion.div
              key={cornerCrackerKey}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ 
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: 'none',
                zIndex: 15
              }}
            >
              {/* Four Corner Crackers */}
              {cornerPositions.map((position, cornerIndex) => (
                <div key={cornerIndex} style={{ ...position, position: 'absolute' }}>
                  {[...Array(18)].map((_, i) => ( // More particles per corner
                    <CornerCrackerParticle
                      key={`corner-${cornerIndex}-${i}`}
                      style={{
                        background: crackerColors[i % crackerColors.length],
                        width: Math.random() * 15 + 8, // Larger particles
                        height: Math.random() * 15 + 8,
                        left: 0,
                        top: 0,
                        boxShadow: `0 0 15px ${crackerColors[i % crackerColors.length]}`,
                      }}
                      variants={cornerCrackerVariants}
                      initial="initial"
                      animate="animate"
                      custom={{ corner: cornerIndex, index: i }}
                    />
                  ))}
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <ContentWrapper>
          <Container maxWidth="lg" sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AnimatePresence mode="wait">
              {/* Countdown Phase */}
              {currentStep === 'countdown' && (
                <motion.div
                  key="countdown"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.2 }}
                  transition={{ duration: 1.5, ease: "easeOut" }} // Extended transition
                  style={{ textAlign: 'center' }}
                >
                  <Box sx={{ mb: 6, display: 'flex', justifyContent: 'center', position: 'relative' }}>
                    <PulseRing
                      animate={{ 
                        scale: [1, 1.5, 1],
                        opacity: [0.8, 0.2, 0.8]
                      }}
                      transition={{ duration: 3, repeat: Infinity }} // Extended pulse
                    />
                    <PulseRing
                      animate={{ 
                        scale: [1, 1.8, 1],
                        opacity: [0.6, 0.1, 0.6]
                      }}
                      transition={{ duration: 3.5, repeat: Infinity, delay: 1 }} // Extended pulse
                    />
                    
                    <motion.div
                      variants={iconVariants}
                      initial="initial"
                      animate="shake"
                    >
                      <HospitalIcon>
                        <LocalHospital sx={{ fontSize: 60, color: 'white' }} />
                      </HospitalIcon>
                    </motion.div>
                  </Box>

                  <motion.div
                    key={countdownNumber}
                    initial={{ scale: 0, opacity: 0, y: 80 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0, opacity: 0, y: -80 }}
                    transition={{ duration: 0.8, ease: "easeOut" }} // Extended number transition
                  >
                    <Typography variant="h1" sx={{ 
                      fontSize: '10rem', // Larger font
                      fontWeight: 'bold', 
                      color: '#145db8', 
                      mb: 3,
                      textShadow: '3px 3px 12px rgba(233, 30, 99, 0.4)'
                    }}>
                      {countdownNumber > 0 ? countdownNumber : ""}
                    </Typography>
                  </motion.div>

                  <motion.div
                    animate={{ 
                      opacity: [0.5, 1, 0.5],
                      scale: [0.98, 1.02, 0.98]
                    }}
                    transition={{ duration: 2, repeat: Infinity }} // Extended pulsing
                  >
                    <Typography variant="h5" sx={{ 
                      color: '#145db8', 
                      fontWeight: 600,
                      textShadow: '2px 2px 6px rgba(233, 30, 99, 0.3)',
                      fontSize: '1.8rem'
                    }}>
                      System Initializing...
                    </Typography>
                  </motion.div>
                </motion.div>
              )}

              {/* Text Reveal Phase */}
              {currentStep === 'reveal' && (
                <motion.div
                  key="reveal"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1.5, ease: "easeOut" }} // Extended transition
                  style={{ textAlign: 'center', width: '100%' }}
                >
                  <Container maxWidth="lg">
                    <Box sx={{ mb: 3 }}>
                      <motion.div
                        animate={{
                          rotate: [0, 360],
                          scale: [1, 1.08, 1],
                        }}
                        transition={{
                          duration: 4, // Extended rotation
                          ease: "easeInOut",
                          repeat: Infinity,
                          repeatDelay: 3
                        }}
                      >
                        <Avatar 
                          sx={{ 
                            width: 140, 
                            height: 140, 
                            mx: 'auto', 
                            mb: 4,
                            background: 'linear-gradient(135deg, #145db8, #ff6b9d)',
                            boxShadow: '0 15px 50px rgba(233, 30, 99, 0.5)'
                          }}
                        >
                          <LocalHospital sx={{ fontSize: 70 }} />
                        </Avatar>
                      </motion.div>
                      <Typography variant="h2" sx={{ 
                        color: '#145db8', 
                        fontWeight: 'bold', 
                        textShadow: '2px 2px 4px rgba(233, 30, 99, 0.3)',
                        fontSize: '3rem'
                      }}>
                        🎊 SYSTEM LAUNCHED 🎊
                      </Typography>
                    </Box>

                    <Grid container spacing={4} justifyContent="center">
                      <Grid item xs={12} lg={10}>
                        <AnimatePresence>
                          {textStep >= 1 && (
                            <motion.div
                              variants={textVariants}
                              initial="initial"
                              animate="animate"
                            >
                              <Box sx={{ mb: 1, textAlign: 'center' }}>
                                <Typography variant="h4" sx={{ 
                                  color: '#145db8', 
                                  fontWeight: 600, 
                                  fontSize: '2rem',
                                  textShadow: '1px 1px 3px rgba(233, 30, 99, 0.2)'
                                }}>
                                  அரசு மோகன் குமாரமங்கலம் மருத்துவக் கல்லூரி மருத்துவமனை சேலம் -1
                                </Typography>
                                <Typography variant="h6" sx={{ 
                                  color: '#666', 
                                  fontWeight: 400,
                                  fontSize: '1.4rem'
                                }}>
                                  Government Mohan Kumaramangalam Medical College Hospital Salem - 1
                                </Typography>
                              </Box>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Grid>

                      <Grid item xs={12} lg={10}>
                        <AnimatePresence>
                          {textStep >= 2 && (
                            <motion.div
                              variants={textVariants}
                              initial="initial"
                              animate="animate"
                            >
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{ 
                                  color: '#145db8', 
                                  fontWeight: 600, 
                                  fontSize: '2rem',
                                  textShadow: '1px 1px 3px rgba(233, 30, 99, 0.2)'
                                }}>
                                  உள்நோயாளி வருகையாளர் மேலாண்மை டாஷ்போர்டு
                                </Typography>
                                <Typography variant="h6" sx={{ 
                                  color: '#666', 
                                  fontWeight: 400,
                                  fontSize: '1.4rem'
                                }}>
                                  Inpatient Visitor Management Dashboard
                                </Typography>
                              </Box>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Grid>

                      <Grid item xs={12} lg={8}>
                        <AnimatePresence>
                          {textStep >= 3 && (
                            <motion.div
                              variants={textVariants}
                              initial="initial"
                              animate="animate"
                            >
                              <Box sx={{ 
                                textAlign: 'center',
                                py: 5,
                                mb: 3,
                              }}>
                                <motion.div
                                  animate={{
                                    scale: [1, 1.15, 1],
                                    rotate: [0, 8, -8, 0],
                                  }}
                                  transition={{
                                    duration: 3.5, // Extended animation
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                  }}
                                >
                                  <LocalHospital sx={{ fontSize: 100, mb: 4, color: '#145db8' }} />
                                </motion.div>
                                <Typography variant="h3" sx={{ 
                                  fontWeight: 'bold', 
                                  mb: 2,
                                  color: '#145db8',
                                  textShadow: '3px 3px 6px rgba(233, 30, 99, 0.3)'
                                }}>
                                  🌟 READY TO SERVE 🌟
                                </Typography>
                                <Typography variant="h6" sx={{ 
                                  color: '#666', 
                                  fontWeight: 400,
                                  mb:2,
                                  fontSize: '1.2rem'
                                }}>
                                  Redirecting to Login Portal...
                                </Typography>
                                <motion.div
                                  animate={{
                                    opacity: [0.7, 1, 0.7],
                                    scale: [0.98, 1.02, 0.98]
                                  }}
                                  transition={{
                                    duration: 2.5,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                  }}
                                >
                                  <Box sx={{
                                    width: '100%',
                                    height: '6px',
                                    background: 'linear-gradient(90deg, #145db8, #ff6b9d, #145db8)',
                                    borderRadius: '3px',
                                    mt: 2,
                                    position: 'relative',
                                    overflow: 'hidden'
                                  }}>
                                    <motion.div
                                      style={{
                                        width: '30%',
                                        height: '100%',
                                        background: 'linear-gradient(90deg, transparent, #fff, transparent)',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                      }}
                                      animate={{
                                        x: ['-100%', '370%']
                                      }}
                                      transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                      }}
                                    />
                                  </Box>
                                </motion.div>
                              </Box>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Grid>
                    </Grid>
                  </Container>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Central Cracker Particles */}
            <AnimatePresence>
              {showParticles && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  style={{ 
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    zIndex: 10
                  }}
                >
                  {[...Array(50)].map((_, i) => (
                    <CrackerParticle
                      key={i}
                      style={{
                        background: crackerColors[i % crackerColors.length],
                        width: Math.random() * 20 + 10,
                        height: Math.random() * 20 + 10,
                        boxShadow: `0 0 20px ${crackerColors[i % crackerColors.length]}`,
                      }}
                      variants={crackerVariants}
                      initial="initial"
                      animate="animate"
                      custom={i}
                    />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Additional Sparkle Burst */}
            <AnimatePresence>
              {showFinalBurst && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  style={{ 
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    pointerEvents: 'none',
                    zIndex: 8
                  }}
                >
                  {[...Array(100)].map((_, i) => (
                    <SparkleElement
                      key={`burst-${i}`}
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        width: `${6 + Math.random() * 8}px`,
                        height: `${6 + Math.random() * 8}px`,
                        background: crackerColors[i % crackerColors.length],
                        boxShadow: `0 0 15px ${crackerColors[i % crackerColors.length]}`,
                      }}
                      variants={sparkleVariants}
                      initial="initial"
                      animate="animate"
                      custom={i}
                    />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </Container>
        </ContentWrapper>
      </MainContainer>
    </ThemeProvider>
  );
};

export default CrackerLaunchComponent;